import * as React from 'react'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Hint, Text } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'

import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'
import { locale } from 'modules/marketplace_modules/components/utils/locales'
import { useGetDatoModules } from 'modules/marketplace_modules/hooks/useGetDatoModules'
import UpsellModalModulesConfirmation from 'modules/upsell/components/UpsellModalModulesConfirmation'
import UpsellModalError from 'modules/upsell/components/UpsellModalModulesError'
import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { getColorScheme } from 'modules/upsell/utils/colorSchemeUpsellModule'

import ModalFooter from './_ModalFooter'
import ModalHeader from './_ModalHeader'
import ModuleInfo from './_ModuleInfo'
import PerksList from './_PerksList'
import { DatoModule } from './types'

interface Props {
  moduleName: string
  requestSource?: string
  featureIdentifier?: string
  requestReason: ValidReasons
  requestComponent?: string
  variant: string | null
  onClose: () => void
  onError: () => void
  onSuccess?: (id?: number | null) => void
  moreInfo?: string
  pageSource?: string
}

const UpsellModalModules = ({
  moduleName,
  requestSource,
  featureIdentifier,
  requestReason,
  requestComponent,
  variant,
  pageSource,
  onClose,
  onError,
  onSuccess,
}: Props) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = React.useState(false)
  const [isErrorOpen, setIsErrorOpen] = React.useState(false)
  const [isRequesting, setIsRequesting] = React.useState(false)
  const [showMainModal, setShowMainModal] = React.useState(true)
  const [shouldShowSharedSession, setShouldShowSharedSession] = React.useState(false)

  const { create } = useCreateUpsellRequest()
  const session = useCurrentSession()
  const { navigation } = useNavigation()
  const { pathname } = useLocation()
  const mainCountry = session.company.legalEntities.find((legalEntity) => legalEntity.main)?.country

  const countryCodeForCompany = countryCode(mainCountry || '')

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const { data, loading, error } = useGetDatoModules({
    chargebeeModuleName: moduleName,
    countryCode: countryCodeForCompany,
    locale: locale[session.access.locale],
  }) as { data: DatoModule[]; loading: boolean; error: Error }

  const { seatCount } = session.company.subscription

  const handleSharedSessionVisibility = (showSharedSession: boolean, maxSize?: string) => {
    const MARKETS = ['es']
    const defaultMaxSize = maxSize || '45'
    const isSharedSessionSegment = seatCount <= Number(defaultMaxSize)

    setShouldShowSharedSession(
      MARKETS.includes(mainCountry || '') && showSharedSession && isSharedSessionSegment
    )
  }

  useEffect(() => {
    if (!error && !loading) {
      track(trackingEvents.upsell.openRequestUpgradeModal, {
        module_requested: moduleName,
        feature_request: featureIdentifier,
        request_source: requestSource,
        request_reason: requestReason,
        page_path: pathname,
        is_am_assigned: false,
        variant: variant,
        page_source: pageSource,
        request_component: requestComponent,
      })
      handleSharedSessionVisibility(data[0]?.showSharedSession ?? false, data[0]?.maxCompanySize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error])

  if (error) {
    onError()
    return null
  }

  const handleRequestInfo = async () => {
    setIsRequesting(true)
    const result = await create({
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      moduleRequested: moduleName as AvailableModules,
      requestSource: requestSource,
      requestReason,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      featureRequested: featureIdentifier as Features,
      meetingBooked: false,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      variant: variant as 'A' | 'B' | 'revamp',
      pageSource,
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      setIsRequesting(false)
      setIsConfirmationOpen(true)
      setShowMainModal(false)
      onSuccess?.(handled.data?.upsellRequest?.id)
    } else {
      setIsErrorOpen(true)
    }
  }

  const handleFinalClose = () => {
    setShowMainModal(false)
    onClose()
  }

  if (loading) return null

  const moduleData = data[0]
  const colorScheme = getColorScheme(moduleData?.category.name)

  return (
    <>
      {showMainModal && (
        <Hint.NewModal onClose={handleFinalClose} onFinish={handleFinalClose}>
          <Box
            flexDirection="column"
            width="full"
            style={{
              background: `radial-gradient(70% 30% at 90% 10%, ${colorScheme.gradientStart} 0%, rgb(255, 255, 255) 80%)`,
            }}
          >
            <Box padding="s16">
              <Text.Header3>{moduleData?.upsellTitle}</Text.Header3>
            </Box>
            <Box flexDirection="row" width="full">
              <ModalHeader imageUrl={moduleData?.upsellImage?.url} />

              <Box gap="s24" justifyContent="center">
                <ModuleInfo
                  icon={moduleData?.icon}
                  name={moduleData?.translatedName}
                  description={moduleData?.upsellDescription}
                  iconBackground={colorScheme.iconBackground}
                />
                <PerksList perks={moduleData?.upsellPerks} />
              </Box>
            </Box>
            <ModalFooter
              onRequestInfo={handleRequestInfo}
              navigation={navigation}
              slug={moduleData?.slug}
              moduleName={moduleName}
              onClose={handleFinalClose}
              isRequesting={isRequesting}
            />
          </Box>
        </Hint.NewModal>
      )}

      {isConfirmationOpen && (
        <UpsellModalModulesConfirmation
          onClose={() => {
            setIsConfirmationOpen(false)
            onClose()
          }}
          sharedSession={{
            showSharedSession: shouldShowSharedSession,
            moduleName: moduleData?.name,
            sharedSessionLink: moduleData?.sharedSessionLink,
            sharedSessionTitle: moduleData?.sharedSessionTitle,
          }}
        />
      )}

      {isErrorOpen && <UpsellModalError onClose={() => setIsErrorOpen(false)} />}
    </>
  )
}

export default UpsellModalModules
